<template>
  <div class="badge" :class="[props.variant, props.size]">
    <div class="dot" v-if="props.dot"></div>
    <Icon v-if="props.icon" :name="props.icon" :size="iconSize" class="icon" />
    <div class="label" v-if="props.label">{{ props.label }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { IconName } from '../../values/icons.const';
import Icon from '../icon.vue';
import { BadgeVariant } from './badge-variant.type';

interface BadgeProps {
  label?: string;
  variant?: BadgeVariant;
  dot?: boolean;
  size?: 'small' | 'medium';
  icon?: IconName;
}

const props = withDefaults(defineProps<BadgeProps>(), {
  variant: 'ghost',
  dot: false,
  size: 'medium',
});

const iconSize = computed(() => (props.size === 'medium' ? 'small' : 'extra-small'));
</script>

<style scoped lang="scss">
.badge {
  display: flex;
  align-items: center;
  gap: var(--t-spacing-xs);

  padding: var(--t-spacing-xs) var(--t-spacing-sm);

  width: fit-content;
  max-height: fit-content;

  border-radius: var(--t-border-radius-xs);

  // Default colors - icons inherit via currentColor
  color: var(--t-color-neutral-950);

  .dot {
    width: var(--t-spacing-sm);
    height: var(--t-spacing-sm);
    border-radius: var(--t-border-radius-half-rounded);
  }

  .icon {
    flex-shrink: 0;
  }

  .label {
    color: var(--t-color-neutral-950);
    font: var(--t-typography-label-md-font);
  }

  &.small {
    .label {
      font: var(--t-typography-label-sm-font);
    }
  }

  &.grey {
    background-color: var(--t-color-neutral-200);

    .dot {
      background-color: var(--t-color-neutral-600);
    }
  }

  &.green {
    background-color: var(--t-color-green-200);

    .dot {
      background-color: var(--t-color-green-500);
    }
  }

  &.red {
    background-color: var(--t-color-red-200);

    .dot {
      background-color: var(--t-color-red-600);
    }
  }

  &.yellow {
    background-color: var(--t-color-yellow-200);

    .dot {
      background-color: var(--t-color-yellow-500);
    }
  }

  &.orange {
    background-color: var(--t-color-orange-200);

    .dot {
      background-color: var(--t-color-orange-400);
    }
  }

  &.blue {
    background-color: var(--t-color-blue-200);

    .dot {
      background-color: var(--t-color-blue-500);
    }
  }

  &.violet {
    background-color: var(--t-color-violet-200);

    .dot {
      background-color: var(--t-color-violet-600);
    }
  }

  &.pink {
    background-color: var(--t-color-pink-200);

    .dot {
      background-color: var(--t-color-pink-500);
    }
  }

  &.pink-dust {
    background-color: var(--t-color-pink-dust-200);

    .dot {
      background-color: var(--t-color-pink-dust-500);
    }
  }

  &.blue-dust {
    background-color: var(--t-color-blue-dust-200);

    .dot {
      background-color: var(--t-color-blue-dust-700);
    }
  }

  &.teal {
    background-color: var(--t-color-teal-200);

    .dot {
      background-color: var(--t-color-teal-500);
    }
  }

  &.olive {
    background-color: var(--t-color-olive-200);

    .dot {
      background-color: var(--t-color-olive-500);
    }
  }
}
</style>
